/**
 * A registration, wherever it was typed.
 *
 * Two places in the header take a reg: the "Your car" band, which asks for one,
 * and the search box, which does not — but which is the most familiar-looking
 * input on the page, so some people will put one there anyway. A reg typed into
 * search matches nothing at all: no product title contains a registration
 * number, so the query returns zero results and the visit ends on the one
 * screen that cannot help. This module is what both of them share so the answer
 * is the same either way.
 *
 * Deliberately NOT a custom element — it is three functions, and the two
 * elements that need it are rendered by the same section.
 */
window.AirfluxPlate = window.AirfluxPlate || {
  /**
   * Does this look like a UK registration?
   *
   * Four formats, and pointedly not the fifth. Dateless plates (`ABC 123`,
   * `123 ABC`) are left out because the pattern that matches them also matches
   * half the vocabulary of this catalogue: `A4`, `G20`, `E90`, `F30`, `W124`,
   * `MK7` are cars and body codes people search for, not registrations. Every
   * format below is at least five characters and collides with none of them.
   *
   * Being wrong is cheap in one direction and expensive in the other: a missed
   * reg is a zero-result search, a false positive is an extra row offered above
   * results that still appear. The list errs towards missing.
   */
  looksLikeReg(value) {
    const reg = String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    return (
      /^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(reg) || // current, 2001–     AB12 CDE
      /^[A-Z][0-9]{1,3}[A-Z]{3}$/.test(reg) || //  prefix, 1983–2001  A123 BCD
      /^[A-Z]{3}[0-9]{1,3}[A-Z]$/.test(reg) || //  suffix, 1963–1983  ABC 123D
      /^[A-Z]{3}[0-9]{3,4}$/.test(reg) //          Northern Ireland   ABC 1234
    );
  },

  /** `ab12cde` → `AB12 CDE`, so what we echo back is a plate, not a string. */
  format(value) {
    const reg = String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    return /^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(reg) ? `${reg.slice(0, 4)} ${reg.slice(4)}` : reg;
  },

  /**
   * Give the reg to something that can resolve it. A finder on this page gets
   * it in place, which keeps the customer where they were; a page without one
   * falls back to whatever the caller names — today `/pages/my-car`, which
   * carries a finder AND is about nothing but the answer — with the reg in the
   * query string for `airflux-fitment.js` to pick up on arrival.
   */
  handOff(reg, { home = '/' } = {}) {
    const value = String(reg || '').trim();
    if (!value) return;

    /**
     * 🔴 A finder in the page's OWN content, not just any finder on the page.
     *
     * `document.querySelector('airflux-fitment')` used to be enough, and stopped
     * being enough the day the closing CTA moved into `footer-group.json`: from
     * then on EVERY page carried a finder, so "keep the customer where they
     * were" quietly became "scroll the customer to the bottom of the page they
     * were on". A reg typed in the header of the About page answered itself in
     * the footer, three sections past anything to do with the question.
     *
     * Where the finder is the page — the home hero, the two landing pages, a
     * product page — resolving in place is still exactly right and still what
     * happens. Where the only finder is the site-wide footer CTA, the reg is a
     * new subject and deserves the page that is about it.
     */
    const widget = [...document.querySelectorAll('airflux-fitment')].find(
      (el) => el.querySelector('[data-airflux-reg]') && !el.closest('.shopify-section-group-footer-group')
    );
    const input = widget?.querySelector('[data-airflux-reg]');

    if (!widget || !input) {
      window.location.href = `${home}?reg=${encodeURIComponent(value)}`;
      return;
    }

    input.value = value;
    widget.scrollIntoView({
      block: 'center',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
    widget.querySelector('[data-airflux-plate-submit]')?.click();
  },
};
